/**
 * SPBFilterPanel — replicates the old VESL dashboard filter layout.
 * Single flex-wrap row: Product | Geo | Device | Target(align+dates) | Compare(mode+dates) | Apply | Toggle
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import type { PluginFilterPanelProps, DashboardFilters } from "../../types";
import type { AlignmentType, ComparisonModeType } from "../../utils/dateUtils";
import { alignDates, computeBaselineDates } from "../../utils/dateUtils";
import {
  fetchMarketplaces,
  fetchRegions,
} from "../../services/dashboardService";
import type { Marketplace, Region } from "../../utils/marketplaceUtils";

function getDefaultWBRWeek(): { start: string; end: string } {
  const now = new Date();
  const wbrDate = new Date(now);
  wbrDate.setDate(wbrDate.getDate() - 7);
  const dayOfWeek = wbrDate.getDay();
  const sunday = new Date(wbrDate);
  sunday.setDate(wbrDate.getDate() - dayOfWeek);
  const saturday = new Date(sunday);
  saturday.setDate(sunday.getDate() + 6);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { start: fmt(sunday), end: fmt(saturday) };
}

const defaultWeek = getDefaultWBRWeek();

const DEFAULT_FILTERS: DashboardFilters = {
  product: "SP",
  geo: "ww",
  device: "Desktop-Mobile",
  targetStartDate: defaultWeek.start,
  targetEndDate: defaultWeek.end,
  baselineStartDate: "",
  baselineEndDate: "",
  alignment: "D",
  comparisonMode: "YoY",
  secondTableEnabled: false,
  currency: "USD",
};

interface GeoGroup {
  label: string;
  regionCode: string;
  marketplaces: { id: string; label: string }[];
}

const SPBFilterPanel: React.FC<PluginFilterPanelProps> = ({
  filters: rawFilters,
  onFiltersChange,
  onApply,
  isLoading,
}) => {
  const filters: DashboardFilters = React.useMemo(
    () => ({
      ...DEFAULT_FILTERS,
      ...(rawFilters as Partial<DashboardFilters>),
    }),
    [rawFilters]
  );
  const [regions, setRegions] = useState<Region[]>([]);
  const [, setMarketplaces] = useState<Marketplace[]>([]);
  const [geoGroups, setGeoGroups] = useState<GeoGroup[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [mktData, regData] = await Promise.all([
          fetchMarketplaces(),
          fetchRegions(),
        ]);
        if (cancelled) return;
        setMarketplaces(mktData);
        setRegions(regData);
      } catch (err) {
        console.warn("Failed to fetch geo data:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (regions.length === 0) return;
    const regionOrder = ["na", "eu", "eu5", "row"];
    const regionLabels: Record<string, string> = {
      na: "North America",
      eu: "Europe",
      eu5: "EU5",
      row: "Rest-of-World",
    };
    const groups: GeoGroup[] = [];
    for (const code of regionOrder) {
      const region = regions.find((r) => r.region_code === code);
      if (!region) continue;
      let parsed: any = region.marketplaces;
      while (typeof parsed === "string") {
        try {
          parsed = JSON.parse(parsed);
        } catch {
          parsed = [];
          break;
        }
      }
      const mktList = Array.isArray(parsed) ? parsed : [];
      groups.push({
        label: regionLabels[code] ?? region.display_name,
        regionCode: code,
        marketplaces: mktList.map((m: any) => ({
          id: String(m.marketplace_id),
          label: `${m.marketplace_id} (${m.country_code})`,
        })),
      });
    }
    setGeoGroups(groups);
  }, [regions]);

  useEffect(() => {
    const baseline = computeBaselineDates(
      filters.targetStartDate,
      filters.targetEndDate,
      filters.comparisonMode
    );
    onFiltersChange({
      ...filters,
      baselineStartDate: baseline.start,
      baselineEndDate: baseline.end,
    } as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = useCallback(
    (partial: Partial<DashboardFilters>) => {
      onFiltersChange({ ...filters, ...partial } as any);
    },
    [filters, onFiltersChange]
  );

  const handleAlignment = useCallback(
    (_: React.MouseEvent<HTMLElement>, value: AlignmentType | null) => {
      if (!value) return;
      const aligned = alignDates(
        filters.targetStartDate,
        filters.targetEndDate,
        value
      );
      const baseline = computeBaselineDates(
        aligned.start,
        aligned.end,
        filters.comparisonMode
      );
      update({
        alignment: value,
        targetStartDate: aligned.start,
        targetEndDate: aligned.end,
        baselineStartDate: baseline.start,
        baselineEndDate: baseline.end,
      });
    },
    [filters, update]
  );

  const handleComparisonMode = useCallback(
    (_: React.MouseEvent<HTMLElement>, value: ComparisonModeType | null) => {
      if (!value) return;
      const baseline = computeBaselineDates(
        filters.targetStartDate,
        filters.targetEndDate,
        value
      );
      update({
        comparisonMode: value,
        baselineStartDate: baseline.start,
        baselineEndDate: baseline.end,
      });
    },
    [filters, update]
  );

  const isCustom = filters.comparisonMode === "Custom";
  const tgSx = {
    "& .MuiToggleButton-root": {
      px: 1,
      py: 0.3,
      textTransform: "none",
      fontSize: "0.75rem",
      lineHeight: 1.4,
    },
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        p: 2,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        alignItems: "flex-end",
      }}
    >
      {/* Product */}
      <FormControl size="small" sx={{ minWidth: 90 }}>
        <InputLabel id="spb-product-label">Product</InputLabel>
        <Select
          labelId="spb-product-label"
          value={filters.product}
          label="Product"
          onChange={(e: SelectChangeEvent) =>
            update({ product: e.target.value as "SP" | "SB" })
          }
        >
          <MenuItem value="SP">SP</MenuItem>
          <MenuItem value="SB">SB</MenuItem>
        </Select>
      </FormControl>

      {/* Geo */}
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel id="spb-geo-label">Geo</InputLabel>
        <Select
          labelId="spb-geo-label"
          value={filters.geo}
          label="Geo"
          onChange={(e: SelectChangeEvent) => update({ geo: e.target.value })}
        >
          <MenuItem value="ww">Worldwide</MenuItem>
          {geoGroups.map((group) => [
            <ListSubheader key={`hdr-${group.regionCode}`}>
              {group.label}
            </ListSubheader>,
            <MenuItem
              key={group.regionCode}
              value={group.regionCode}
              sx={{ pl: 3 }}
            >
              {group.label} (aggregate)
            </MenuItem>,
            ...group.marketplaces.map((m) => (
              <MenuItem key={m.id} value={m.id} sx={{ pl: 5 }}>
                {m.label}
              </MenuItem>
            )),
          ])}
        </Select>
      </FormControl>

      {/* Device */}
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel id="spb-device-label">Device</InputLabel>
        <Select
          labelId="spb-device-label"
          value={filters.device}
          label="Device"
          onChange={(e: SelectChangeEvent) =>
            update({ device: e.target.value })
          }
        >
          <MenuItem value="Desktop-Mobile">Desktop+Mobile</MenuItem>
          <MenuItem value="Desktop">Desktop</MenuItem>
          <MenuItem value="Mobile">Mobile</MenuItem>
        </Select>
      </FormControl>

      {/* Target Date: alignment buttons + date inputs stacked */}
      <Stack spacing={0.5}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{ whiteSpace: "nowrap" }}
          >
            Target Date
          </Typography>
          <ToggleButtonGroup
            value={filters.alignment}
            exclusive
            onChange={handleAlignment}
            size="small"
            sx={tgSx}
          >
            {(["D", "W", "M", "Q", "H", "Y"] as AlignmentType[]).map((a) => (
              <ToggleButton key={a} value={a}>
                {a}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <TextField
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filters.targetStartDate}
            sx={{ width: 145 }}
            onChange={(e) => {
              const aligned = alignDates(
                e.target.value,
                filters.targetEndDate,
                filters.alignment
              );
              const baseline = computeBaselineDates(
                aligned.start,
                aligned.end,
                filters.comparisonMode
              );
              update({
                targetStartDate: aligned.start,
                targetEndDate: aligned.end,
                baselineStartDate: baseline.start,
                baselineEndDate: baseline.end,
              });
            }}
          />
          <Typography variant="caption" color="text.secondary">
            to
          </Typography>
          <TextField
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filters.targetEndDate}
            sx={{ width: 145 }}
            onChange={(e) => {
              const baseline = computeBaselineDates(
                filters.targetStartDate,
                e.target.value,
                filters.comparisonMode
              );
              update({
                targetEndDate: e.target.value,
                baselineStartDate: baseline.start,
                baselineEndDate: baseline.end,
              });
            }}
          />
        </Stack>
      </Stack>

      {/* Compare: comparison buttons + baseline dates stacked */}
      <Stack spacing={0.5}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{ whiteSpace: "nowrap" }}
          >
            Compare
          </Typography>
          <ToggleButtonGroup
            value={filters.comparisonMode}
            exclusive
            onChange={handleComparisonMode}
            size="small"
            sx={tgSx}
          >
            {(
              [
                "YoY",
                "HoH",
                "QoQ",
                "MoM",
                "WoW",
                "DoD",
                "Custom",
              ] as ComparisonModeType[]
            ).map((m) => (
              <ToggleButton key={m} value={m}>
                {m}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <TextField
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filters.baselineStartDate}
            disabled={!isCustom}
            sx={{ width: 145 }}
            onChange={(e) => update({ baselineStartDate: e.target.value })}
          />
          <Typography variant="caption" color="text.secondary">
            to
          </Typography>
          <TextField
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filters.baselineEndDate}
            disabled={!isCustom}
            sx={{ width: 145 }}
            onChange={(e) => update({ baselineEndDate: e.target.value })}
          />
        </Stack>
      </Stack>

      {/* Apply */}
      <Button
        variant="contained"
        onClick={onApply}
        disabled={
          isLoading || !filters.targetStartDate || !filters.targetEndDate
        }
        sx={{
          height: 40,
          px: 3,
          textTransform: "none",
          fontWeight: 600,
          bgcolor: "#4CAF50",
          "&:hover": { bgcolor: "#45a049" },
        }}
      >
        Apply Filters
      </Button>

      {/* Comparison toggle */}
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Switch
          checked={filters.secondTableEnabled}
          onChange={(e) => update({ secondTableEnabled: e.target.checked })}
          size="small"
        />
        <Typography variant="body2">Enable Comparison</Typography>
      </Stack>

      {/* Currency toggle — LOC only available for individual marketplaces */}
      {(() => {
        const aggregateGeos = new Set(["ww", "na", "eu", "eu5", "row"]);
        const isAggregate = aggregateGeos.has(filters.geo ?? "ww");
        // If user switches to an aggregate geo, reset currency to USD
        if (isAggregate && filters.currency === "LOC") {
          update({ currency: "USD" });
        }
        return (
          <Stack spacing={0.5}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
            >
              Currency
            </Typography>
            <ToggleButtonGroup
              value={isAggregate ? "USD" : filters.currency ?? "USD"}
              exclusive
              onChange={(
                _: React.MouseEvent<HTMLElement>,
                value: "USD" | "LOC" | null
              ) => {
                if (value) update({ currency: value });
              }}
              size="small"
              sx={tgSx}
            >
              <ToggleButton value="USD">USD</ToggleButton>
              <ToggleButton value="LOC" disabled={isAggregate}>
                LOC
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        );
      })()}
    </Box>
  );
};

export default SPBFilterPanel;
